import { httpClient } from '@/api/httpClient';
import { mapPaginatedResource, type PaginatedApiResource, type PaginatedResult } from '@/api/pagination.types';
import {
  mapApiResourceToDeviceSetting,
  mapApiResourceToDeviceSettingsHistoryEntry,
  mapApiResourceToPerformedTreatment,
  mapApiResourceToSessionNote,
  mapApiResourceToSessionNoteSummary,
  mapSessionNoteWritePayload,
  type DeviceSetting,
  type DeviceSettingApiResource,
  type DeviceSettingsHistoryEntry,
  type DeviceSettingsHistoryEntryApiResource,
  type PerformedTreatment,
  type PerformedTreatmentApiResource,
  type SessionNote,
  type SessionNoteApiResource,
  type SessionNoteSummary,
  type SessionNoteSummaryApiResource,
  type SessionNoteWritePayload,
} from './sessionNotesApi.types';

export async function fetchPatientSessionNotes(
  patientId: string,
  page: number,
  pageSize: number,
  status: string | null
): Promise<PaginatedResult<SessionNoteSummary>> {
  const response = await httpClient.get<PaginatedApiResource<SessionNoteSummaryApiResource>>(
    '/patients/' + patientId + '/session-notes',
    { params: { page, page_size: pageSize, status: status === null ? undefined : status } }
  );
  return mapPaginatedResource(response.data, mapApiResourceToSessionNoteSummary);
}

export async function fetchPatientPerformedTreatments(
  patientId: string,
  page: number,
  pageSize: number
): Promise<PaginatedResult<PerformedTreatment>> {
  const response = await httpClient.get<PaginatedApiResource<PerformedTreatmentApiResource>>(
    '/patients/' + patientId + '/treatments',
    { params: { page, page_size: pageSize } }
  );
  return mapPaginatedResource(response.data, mapApiResourceToPerformedTreatment);
}

export async function fetchSessionNote(noteId: string): Promise<SessionNote> {
  const response = await httpClient.get<SessionNoteApiResource>('/session-notes/' + noteId);
  return mapApiResourceToSessionNote(response.data);
}

export async function fetchAppointmentSessionNote(appointmentId: string): Promise<SessionNote> {
  const response = await httpClient.get<SessionNoteApiResource>(
    '/appointments/' + appointmentId + '/session-note'
  );
  return mapApiResourceToSessionNote(response.data);
}

export async function createSessionNote(
  patientId: string,
  payload: SessionNoteWritePayload
): Promise<SessionNote> {
  const response = await httpClient.post<SessionNoteApiResource>(
    '/patients/' + patientId + '/session-notes',
    mapSessionNoteWritePayload(payload)
  );
  return mapApiResourceToSessionNote(response.data);
}

export async function updateSessionNote(
  noteId: string,
  payload: SessionNoteWritePayload
): Promise<SessionNote> {
  const response = await httpClient.patch<SessionNoteApiResource>(
    '/session-notes/' + noteId,
    mapSessionNoteWritePayload(payload)
  );
  return mapApiResourceToSessionNote(response.data);
}

export async function uploadSessionNotePhoto(noteId: string, file: File): Promise<SessionNote> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await httpClient.post<SessionNoteApiResource>(
    '/session-notes/' + noteId + '/photos',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return mapApiResourceToSessionNote(response.data);
}

export async function removeSessionNotePhoto(
  noteId: string,
  photoId: string
): Promise<SessionNote> {
  const response = await httpClient.delete<SessionNoteApiResource>(
    '/session-notes/' + noteId + '/photos/' + photoId
  );
  return mapApiResourceToSessionNote(response.data);
}

export async function fetchLastDeviceSettings(
  patientId: string,
  treatmentId: string
): Promise<DeviceSetting[]> {
  const response = await httpClient.get<DeviceSettingApiResource[]>(
    '/patients/' + patientId + '/treatments/' + treatmentId + '/last-device-settings'
  );
  return response.data.map(mapApiResourceToDeviceSetting);
}

export async function fetchDeviceSettingsHistory(
  patientId: string
): Promise<DeviceSettingsHistoryEntry[]> {
  const response = await httpClient.get<DeviceSettingsHistoryEntryApiResource[]>(
    '/patients/' + patientId + '/device-settings-history'
  );
  return response.data.map(mapApiResourceToDeviceSettingsHistoryEntry);
}
